import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import * as am5 from "@amcharts/amcharts5";
import * as am5percent from "@amcharts/amcharts5/percent";
import * as am5xy from "@amcharts/amcharts5/xy";
import * as am5radar from "@amcharts/amcharts5/radar";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import * as am5plugins_exporting from "@amcharts/amcharts5/plugins/exporting.js";
import { MyTheme } from "./../colors";
import { login } from "../../../../../apis";
import { useTheme } from "@mui/material";

export const DynamicChart = ({
  chartData,
  startDate,
  endDate,
  type = "pie",
  graphicTitle = "Título",
  opacity,
  colors,
}) => {

  const [t] = useTranslation("dashboard");
  const chartDivId = useRef(`chart-${Math.random().toString(36).substring(2, 11)}`);
  const rootRef = useRef(null); // Ref directa al root de amCharts (reemplaza useState + búsqueda en registry)
  const theme= useTheme();

  // Helper local: convierte datos de API {label, count} a formato amCharts {category, value}
  // (usado 4 veces en este archivo: pie/donut, nightingale, rose, bar/column/line)
  const mapChartData = (data) => {
    return data.map((item) => ({
      category: item.label,
      value: item.count,
    }));
  };

  // Helper local: crea leyenda estándar horizontal centrada
  // (usado 3 veces en este archivo: pie/donut, rose, bar/column/line)
  const createStandardLegend = (root, chartInst) => {
    return chartInst.children.push(
      am5.Legend.new(root, {
        centerX: am5.percent(50),
        x: am5.percent(50),
        marginTop: 20,
        layout: root.horizontalLayout,
      })
    );
  };

  const groupDataByYear = (data) => {
    const grouped = {};

    data.forEach((item) => {
      // Normalizamos por filters[0].value ("YYYY-MM-01"), NO por item.label:
      // el label viene en español fijo desde el backend ("Enero"…) y rompería
      // el match si el dashboard está en otro idioma. El nº de mes es estable.
      const [year, month] = item.filters?.[0]?.value?.split("-") ?? [];
      if (!year || !month) return; // Guard: ignora items sin fecha válida en filters
      if (!grouped[year]) {
        grouped[year] = {};
      }

      grouped[year][month] = item.count; // month = "01".."12"
    });

    return grouped;
  };

  useEffect(() => {
    if (!chartData || !chartData?.data) return;

    // Disponer instancia anterior antes de crear una nueva
    if (rootRef.current) {
      rootRef.current.dispose();
      rootRef.current = null;
    }

    const root = am5.Root.new(chartDivId.current);
    rootRef.current = root;
    root.setThemes([MyTheme.new(root), am5themes_Animated.new(root)]);

    let chartInstance;
    let series;

    if (type === "pie" || type === "donut") {
      chartInstance = root.container.children.push(
        am5percent.PieChart.new(root, {
          layout: root.verticalLayout,
          innerRadius: type === "donut" ? am5.percent(50) : 0,
        })
      );


      const gradient = am5.RadialGradient.new(root, {
        stops: [
          { color: am5.color(0xffffff) },
          { color: am5.color(0xffffff) },
          {},
        ],
      });

      series = chartInstance.series.push(
        am5percent.PieSeries.new(root, {
          name: t("dashboard.chart_series_name"),
          valueField: "value",
          categoryField: "category",
          alignLabels: true,
        })
      );
      
      series.slices.template.setAll({
        fillOpacity: opacity,
        strokeOpacity: opacity,
        //fillGradient: gradient,
      });

      series.slices.template.adapters.add("fill", (fill, target) => {
        const dataItem = target.dataItem;
        if (!dataItem) return fill;
      
        const index = series.dataItems.indexOf(dataItem);
        const rawColor = colors?.[index % colors.length];
      
        if (typeof rawColor === "number") {
          return am5.color(rawColor);
        } else if (typeof rawColor === "string" && /^#([0-9a-f]{3}){1,2}$/i.test(rawColor)) {
          return am5.color(rawColor);
        }
      
        return fill;
      });

      // Leyenda estándar (helper local createStandardLegend)
      // Diferencia vs rose y bar: aquí se personalizan labels y valueLabels después
      const legend = createStandardLegend(root, chartInstance);

      legend.labels.template.setAll({
        fontSize: 14,
        fill: am5.color(0x000000),
        paddingRight: 10,
      });
      
      legend.valueLabels.template.setAll({
        fontSize: 14,
        fill: am5.color(0x000000),
      });

      series.slices.template.setAll({
        tooltipText: "{category}: {value}",
      });
      
      // Agrega labels en los slices
      series.bullets.push(function () {
        return am5.Bullet.new(root, {
          sprite: am5.Label.new(root, {
            text: "{value}",
            populateText: true,
            centerX: am5.percent(50),
            centerY: am5.percent(50),
            fill: am5.color(0x000000), // cambia a blanco si usas fondo oscuro
            fontSize: 14,
          }),
        });
      });
      
      // Espera a que los datos estén listos antes de asignarlos
      series.events.on("datavalidated", function () {
        legend.data.setAll(series.dataItems);
      });
    }

    // Cargar datos
    if (series) {
      // Mapeo de datos (helper local mapChartData)
      series.data.setAll(mapChartData(chartData.data));
    // Grafica Nightingale, donde cada valor tiene el mismo grosor de columna, 
    // pero su altura (radio) lo define su valor contra el maximo
    } else if (type === "nightingale") {

      chartInstance = root.container.children.push(
        am5radar.RadarChart.new(root, {
          layout: root.verticalLayout,
          innerRadius: am5.percent(20),
        })
      );

      const xAxis = chartInstance.xAxes.push(
        am5xy.CategoryAxis.new(root, {
          categoryField: "category",
          renderer: am5radar.AxisRendererCircular.new(root, {}),
        })
      );

      const totalSum = chartData.data.reduce((sum, item) => sum + (item.count || 0), 0);
      const maxValue = chartData.data.length > 0
        ? Math.max(...chartData.data.map((item) => item.count || 0))
        : 0;
      const yMax = maxValue <= totalSum * 0.5 ? totalSum / 2 : totalSum;

      const yAxis = chartInstance.yAxes.push(
        am5xy.ValueAxis.new(root, {
          min: 0,
          max: yMax,
          strictMinMax: true,
          renderer: am5radar.AxisRendererRadial.new(root, {}),
        })
      );

      const nightSeries = chartInstance.series.push(
        am5radar.RadarColumnSeries.new(root, {
          name: graphicTitle,
          xAxis,
          yAxis,
          valueYField: "value",
          categoryXField: "category",
          legendLabelText: "{categoryX}",
        })
      );

      nightSeries.columns.template.setAll({
        strokeOpacity: 0,
        fillOpacity: opacity || 0.8,
        cornerRadius: 5, //redondeo de esquinas, recomendado menor a 10
        width: am5.percent(97), //separacion entre dato y dato (100 = sin separacion)
        tooltipText: "{category}: {valueY}",

        // cornerRadiusTL: 12, // no usados por estandarizacion
        // cornerRadiusTR: 12,
        // cornerRadiusBL: 10,
        // cornerRadiusBR: 10,
      });

      nightSeries.columns.template.adapters.add("fill", (fill, target) => {
        const index = nightSeries.dataItems.indexOf(target.dataItem);
        if (index < 0) return fill; // Guard: si no encuentra el dataItem, mantiene color por defecto
        return chartInstance.get("colors").getIndex(index);
      });

      // Mapeo de datos (helper local mapChartData)
      const formattedData = mapChartData(chartData.data);

      xAxis.data.setAll(formattedData);
      nightSeries.data.setAll(formattedData);

      // Leyenda manual con Container simple (sin Legend de AmCharts para evitar dataContext error)
      const legendContainer = chartInstance.children.push(
        am5.Container.new(root, {
          centerX: am5.percent(50),
          x: am5.percent(50),
          marginTop: 20,
          layout: root.horizontalLayout,
        })
      );

      formattedData.forEach((item, index) => {
        const color = chartInstance.get("colors").getIndex(index);
        const legendItem = legendContainer.children.push(
          am5.Container.new(root, {
            layout: root.horizontalLayout,
            paddingRight: 15,
          })
        );
        legendItem.children.push(
          am5.RoundedRectangle.new(root, {
            width: 18,
            height: 18,
            fill: color,
            centerY: am5.percent(50),
            cornerRadiusTL: 3,
            cornerRadiusTR: 3,
            cornerRadiusBL: 3,
            cornerRadiusBR: 3,
          })
        );
        legendItem.children.push(
          am5.Label.new(root, {
            text: item.category,
            fontSize: 14,
            fill: am5.color(theme.palette.text.primary),
            centerY: am5.percent(50),
            paddingLeft: 5,
          })
        );
      });


    // Tipo Rosa
    // Grafica Rose: combina dona  (angulo proporcional al valor) + nightingale (radio proporcional al valor)
    } else if (type === "rose") {

      const totalSum = chartData.data.reduce((sum, item) => sum + (item.count || 0), 0);

      // Mapeo de datos (helper local mapChartData)
      const formattedData = mapChartData(chartData.data);

      chartInstance = root.container.children.push(
        am5percent.PieChart.new(root, {
          layout: root.verticalLayout,
          innerRadius: am5.percent(20),
        })
      );

      const roseSeries = chartInstance.series.push(
        am5percent.PieSeries.new(root, {
          valueField: "value",
          categoryField: "category",
          // tooltipText: "{category}: {value}", 
        })
      );

      // El radio de cada slice se escala proporcionalmente a su valor vs el total
      roseSeries.slices.template.adapters.add("radius", (radius, target) => {
        const dataItem = target.dataItem;
        if (!dataItem) return radius;
        const value = dataItem.get("valueWorking", 0);
        const maxRadius = roseSeries.getPrivate("radius", 100);
        const scale = totalSum > 0 ? value / totalSum : 1;
        return maxRadius * Math.sqrt(scale);
      });

      roseSeries.slices.template.setAll({
        strokeOpacity: 0.5,
        strokeWidth: 1,
        fillOpacity: opacity || 0.8,
        cornerRadius: 5,
      });

      roseSeries.slices.template.adapters.add("fill", (fill, target) => {
        const dataItem = target.dataItem;
        if (!dataItem) return fill;
        const index = roseSeries.dataItems.indexOf(dataItem);
        const rawColor = colors?.[index % (colors?.length || 1)];
        if (typeof rawColor === "number") {
          return am5.color(rawColor);
        } else if (typeof rawColor === "string" && /^#([0-9a-f]{3}){1,2}$/i.test(rawColor)) {
          return am5.color(rawColor);
        }
        return fill;
      });

      // Labels externos con nombre + porcentaje (lineas + texto afuera del slice)
      roseSeries.labels.template.setAll({
        text: "{category}: {valuePercentTotal.formatNumber('0.0')}%",
        fontSize: 14,
      });
      roseSeries.ticks.template.setAll({
        visible: true,
      });

      // Bullets con valor numerico dentro de cada slice
      roseSeries.bullets.push(function () {
        return am5.Bullet.new(root, {
          sprite: am5.Label.new(root, {
            text: "{value}",
            populateText: true,
            centerX: am5.percent(50),
            centerY: am5.percent(50),
            fill: am5.color(theme.palette.text.primary),
            fontSize: 14,
          }),
        });
      });

      roseSeries.data.setAll(formattedData);

      // Leyenda estándar (helper local createStandardLegend)
      const legend = createStandardLegend(root, chartInstance);

      roseSeries.events.on("datavalidated", () => {
        legend.data.setAll(roseSeries.dataItems);
      });

    }else if(type === "bar" || type === "column" || type === "line"){

      chartInstance = root.container.children.push(
        am5xy.XYChart.new(root, {
          layout: root.verticalLayout,
        })
      );
    
      const xAxis = chartInstance.xAxes.push(
        am5xy.CategoryAxis.new(root, {
          categoryField: "category",
          renderer: am5xy.AxisRendererX.new(root, {}),
        })
      );
    
      const yAxis = chartInstance.yAxes.push(
        am5xy.ValueAxis.new(root, {
          renderer: am5xy.AxisRendererY.new(root, {}),
        })
      );
    
      // Agrupar por año si es histórico
      let datasets = [];
      const isGroupedByYear = chartData.type === "historic" && ["line", "bar", "column"].includes(type);

      if (isGroupedByYear) {
        const grouped = groupDataByYear(chartData.data);
        // Etiqueta visible (traducida) por índice; el match con los datos se
        // hace por nº de mes ("01".."12"), no por esta etiqueta.
        const allCategories = [
          t("dashboard.january"),
          t("dashboard.february"),
          t("dashboard.march"),
          t("dashboard.april"),
          t("dashboard.may"),
          t("dashboard.june"),
          t("dashboard.july"),
          t("dashboard.august"),
          t("dashboard.september"),
          t("dashboard.october"),
          t("dashboard.november"),
          t("dashboard.december"),
        ];

        datasets = Object.entries(grouped).map(([year, monthsData]) => ({
          name: year,
          data: allCategories.map((label, i) => {
            const monthKey = String(i + 1).padStart(2, "0"); // "01".."12"
            return {
              category: label,
              value: monthsData[monthKey] ?? 0,
            };
          }),
        }));

        xAxis.data.setAll(allCategories.map((cat) => ({ category: cat })));
      } else {
        // Mapeo de datos (helper local mapChartData)
        const formattedData = mapChartData(chartData.data);

        datasets = [{ name: chartData.title || t("dashboard.default_series_name"), data: formattedData }];
        xAxis.data.setAll(formattedData);
      }
    
      // Factory de series movida fuera del loop — no depende de dataset ni index
      const createSeries = (seriesType, options) => {
        if (seriesType === "column") {
          return am5xy.ColumnSeries.new(root, options);
        } else if (seriesType === "bar") {
          return am5xy.ColumnSeries.new(root, options); // sigue usando ColumnSeries, pero con ejes invertidos
        } else {
          return am5xy.LineSeries.new(root, options);
        }
      };

      datasets.forEach((dataset, index) => {
        const rawColor = colors?.[index % colors.length] || 0x297373;

        const series = chartInstance.series.push(createSeries(type, {
          name: dataset.name,
          xAxis,
          yAxis,
          valueYField: "value",
          categoryXField: "category",
          stroke: am5.color(colors?.[index % colors.length] || 0x297373),
          tooltip: am5.Tooltip.new(root, {
            labelText: "{name}: {valueY}"
          }),
        }));

       /* series.bullets.push(() =>
          am5.Bullet.new(root, {
            sprite: am5.Circle.new(root, {
              radius: 5,
              fill: series.get("stroke"),
              stroke: am5.color(0xffffff),
              strokeWidth: 2,
            }),
          })
        );*/
    
        series.data.setAll(dataset.data);
    
        // Solo si es ColumnSeries
        if (type !== "historic") {
          if (series.columns) {
            series.columns.template.setAll({
              tooltipText: "{category}: {valueY}",
            });
          
            series.columns.template.adapters.add("fill", (fill, target) => {
              const index = series.dataItems.indexOf(target.dataItem);
              return am5.color(colors?.[index % colors.length] || rawColor);
              // Validación defensiva (descomentar cuando se requiera):
              // const rawColor = colors?.[index % colors.length];
              // if (typeof rawColor === "number") {
              //   return am5.color(rawColor);
              // } else if (typeof rawColor === "string" && /^#([0-9a-f]{3}){1,2}$/i.test(rawColor)) {
              //   return am5.color(rawColor);
              // }
              // return fill;
            });
          }
        }

        series.bullets.push(() => am5.Bullet.new(root, {
          locationY: 0,
          sprite: am5.Label.new(root, {
            text: "{valueY}",
            fill: am5.color(0x000000),
            centerY: am5.percent(100),
            centerX: am5.percent(50),
            populateText: true,
          }),
        }));
      });
    
      // Leyenda estándar (helper local createStandardLegend)
      const legend = createStandardLegend(root, chartInstance);

      legend.data.setAll(chartInstance.series.values);
      
    }

    // Exportación
    const exporting = am5plugins_exporting.Exporting.new(root, {
      menu: am5plugins_exporting.ExportingMenu.new(root, {
        align: "right",
        valign: "bottom",
      }),
    });

    // Cleanup: dispose directo via rootRef (sin búsqueda lineal en registry) 
    return () => {
      if (rootRef.current) {
        rootRef.current.dispose();
        rootRef.current = null;
      }
    };
  }, [chartData, startDate, endDate, type]);

  return <div id={chartDivId.current} style={{ width: "100%", height: "500px" }} />;
};

export default DynamicChart;